import React, { useState, useEffect, useMemo } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { getPayments } from "../services/paymentService.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { PaymentCorrectionHost } from "./PaymentCorrection.jsx";

const TABS = [
  { key: "ALL", label: "All" },
  { key: "PAYMENT_IN", label: "Customer Receipts" },
  { key: "PAYMENT_OUT", label: "Vendor Payments" }
];

export function PaymentHistoryModal({
  isOpen,
  onClose,
  // Optional: filter to a specific party (invoice or purchase bill context)
  filterPartyId = null,
  filterType = null, // 'PAYMENT_IN' | 'PAYMENT_OUT' | null
  onViewReceipt, // callback(payment) => void — opens PaymentReceiptModal
  onPaymentsChanged // callback() => void — after a payment is edited or voided (refresh bills)
}) {
  const { defaultCompany } = useCompanies();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(filterType || "ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [editPayment, setEditPayment] = useState(null);
  const [voidingPayment, setVoidingPayment] = useState(null);

  const dateFormat = defaultCompany?.dateFormat || "DD-MM-YYYY";

  const loadPayments = () => {
    setLoading(true);
    return getPayments({ partyId: filterPartyId || null, limitCount: 200 })
      .then(setPayments)
      .catch((err) => console.error("Failed to load payments:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    loadPayments();
  }, [isOpen, filterPartyId]);

  const handlePaymentChanged = async () => {
    await loadPayments();
    if (onPaymentsChanged) onPaymentsChanged();
  };

  const filteredPayments = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return payments.filter((p) => {
      if (activeTab !== "ALL" && p.type !== activeTab) return false;
      if (!term) return true;
      const payNo = (p.paymentNo || "").toLowerCase();
      const party = (p.partyName || p.customerName || p.vendorName || "").toLowerCase();
      const ref = (p.reference || "").toLowerCase();
      return payNo.includes(term) || party.includes(term) || ref.includes(term);
    });
  }, [payments, activeTab, searchTerm]);

  // Voided payments are listed but excluded from totals
  const totalIn = useMemo(() => payments.filter(p => p.type === "PAYMENT_IN" && p.status !== "VOID").reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);
  const totalOut = useMemo(() => payments.filter(p => p.type === "PAYMENT_OUT" && p.status !== "VOID").reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);

  if (!isOpen) return null;

  return (
    <>
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="📜 Payment History"
      size="lg"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        {/* Summary Chips */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "120px", background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: "var(--radius-md)", padding: "0.6rem 0.85rem" }}>
            <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Received (In)</div>
            <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--success)" }}>₹{totalIn.toLocaleString("en-IN")}</div>
          </div>
          <div style={{ flex: 1, minWidth: "120px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "var(--radius-md)", padding: "0.6rem 0.85rem" }}>
            <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Paid Out</div>
            <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--danger)" }}>₹{totalOut.toLocaleString("en-IN")}</div>
          </div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: "flex", gap: "0.3rem", background: "var(--bg-card)", padding: "0.25rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: "0.4rem 0.5rem",
                borderRadius: "calc(var(--radius-md) - 2px)",
                border: "none",
                cursor: "pointer",
                fontSize: "0.78rem",
                fontWeight: "700",
                transition: "background 0.15s ease, color 0.15s ease",
                background: activeTab === tab.key ? "var(--accent-blue, #38bdf8)" : "transparent",
                color: activeTab === tab.key ? "#0f172a" : "var(--text-muted)"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.9rem", pointerEvents: "none" }}>🔍</span>
          <input
            type="text"
            className="form-control"
            placeholder="Search receipt #, party name, or reference…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: "2.2rem" }}
          />
        </div>

        {/* Payment List */}
        {loading && (
          <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "2rem", fontSize: "0.85rem" }}>
            ⏳ Loading payment history…
          </div>
        )}

        {!loading && filteredPayments.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "2rem 1rem" }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>💳</div>
            <div style={{ fontWeight: "600", color: "var(--text-main)" }}>No payments found</div>
            <div style={{ fontSize: "0.82rem", marginTop: "0.25rem" }}>
              {searchTerm ? "No match for your search criteria." : "No payment records yet."}
            </div>
          </div>
        )}

        {!loading && filteredPayments.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {filteredPayments.map((payment) => {
              const isIn = payment.type === "PAYMENT_IN";
              const partyName = payment.partyName || payment.customerName || payment.vendorName || "Party";
              const allocCount = (payment.allocations || []).length;
              const formattedDate = formatAppDate(payment.paymentDate, dateFormat);
              const isVoid = payment.status === "VOID";

              return (
                <div
                  key={payment.id}
                  style={{
                    background: "var(--bg-card)",
                    border: `1px solid ${isVoid ? "var(--border-subtle)" : (isIn ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.18)")}`,
                    borderLeft: `3px solid ${isVoid ? "var(--text-dim)" : (isIn ? "var(--success)" : "var(--danger)")}`,
                    borderRadius: "var(--radius-md)",
                    padding: "0.75rem 1rem",
                    opacity: isVoid ? 0.75 : 1
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: "700", fontSize: "0.88rem", color: "var(--text-main)" }}>
                          {payment.paymentNo || "—"}
                        </span>
                        <Badge
                          variant={isIn ? "success" : "danger"}
                          style={{ fontSize: "0.68rem", padding: "1px 6px" }}
                        >
                          {isIn ? "↙ IN" : "↗ OUT"}
                        </Badge>
                        {isVoid && (
                          <Badge variant="danger" style={{ fontSize: "0.68rem", padding: "1px 6px", fontWeight: "800" }}>
                            VOID
                          </Badge>
                        )}
                        {!isVoid && payment.editCount > 0 && (
                          <span style={{ fontSize: "0.7rem", color: "var(--text-dim)", fontStyle: "italic" }}>edited</span>
                        )}
                        <span style={{ fontSize: "0.76rem", color: "var(--text-dim)" }}>
                          {payment.paymentMode || "Cash"}
                        </span>
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "2px" }}>
                        {isIn ? "From" : "To"}: <strong>{partyName}</strong>
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                        📅 {formattedDate}
                        {allocCount > 0 && ` • ${allocCount} bill${allocCount === 1 ? "" : "s"} settled`}
                        {payment.reference && ` • Ref: ${payment.reference}`}
                      </div>
                      {isVoid && payment.voidReason && (
                        <div style={{ fontSize: "0.75rem", color: "var(--danger)", marginTop: "2px" }}>
                          Voided: {payment.voidReason}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: "800", color: isVoid ? "var(--text-dim)" : (isIn ? "var(--success)" : "var(--danger)"), textDecoration: isVoid ? "line-through" : "none" }}>
                        {isIn ? "+" : "−"}₹{Number(payment.amount || 0).toLocaleString("en-IN")}
                      </div>
                      {onViewReceipt && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => onViewReceipt(payment)}
                          style={{ fontSize: "0.72rem", marginTop: "0.3rem", padding: "2px 8px" }}
                        >
                          📄 Receipt
                        </Button>
                      )}
                      {!isVoid && (
                        <div style={{ display: "flex", gap: "0.3rem", justifyContent: "flex-end", marginTop: "0.3rem" }}>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setEditPayment(payment)}
                            style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                          >
                            ✏️ Edit
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setVoidingPayment(payment)}
                            style={{ fontSize: "0.72rem", padding: "2px 8px", color: "var(--danger)" }}
                          >
                            🚫 Void
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer count */}
        {!loading && filteredPayments.length > 0 && (
          <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", textAlign: "center", paddingTop: "0.25rem" }}>
            Showing {filteredPayments.length} of {payments.length} payment{payments.length === 1 ? "" : "s"}
          </div>
        )}

      </div>
    </BottomSheet>

    <PaymentCorrectionHost
      editPayment={editPayment}
      voidingPayment={voidingPayment}
      onCloseEdit={() => setEditPayment(null)}
      onCloseVoid={() => setVoidingPayment(null)}
      onChanged={handlePaymentChanged}
    />
    </>
  );
}

export default PaymentHistoryModal;
